import React, {
  createContext,
  useContext,
  useState,
  useCallback,
} from "react";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:5001";

const SourcesContext = createContext(null);

const NOOP = {
  sources: [],
  addFileSource: async () => null,
  addTextSource: () => null,
  removeSource: () => {},
  getSourcesContext: () => "",
  hasSourcesLoaded: false,
};

export const useSources = () => {
  const ctx = useContext(SourcesContext);
  if (!ctx) return NOOP;
  return ctx;
};

const generateId = () =>
  Math.random().toString(36).substr(2, 9) + "_" + Date.now();

async function extractPdfText(base64Content, fileName, fileType) {
  try {
    const byteCharacters = atob(base64Content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: fileType });
    const file = new File([blob], fileName, { type: fileType });

    const formData = new FormData();
    formData.append("file", file);

    const res = await fetch(`${API_BASE}/api/process_document`, {
      method: "POST",
      body: formData,
    });
    if (!res.ok) throw new Error("PDF processing failed");

    const data = await res.json();
    let text = "";
    if (data.pages && Array.isArray(data.pages)) {
      text = data.pages.map((p) => p.text).join("\n\n");
    } else if (data.text) {
      text = data.text;
    }
    return text;
  } catch (e) {
    console.error("SourcesContext PDF extract error:", e);
    return "";
  }
}

export const SourcesProvider = ({ children }) => {
  const [sources, setSources] = useState([]);
  const patchSource = useCallback((id, patch) => {
    setSources((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }, []);

  const addFileSource = useCallback(
    async (file) => {
      const id = generateId();
      const sourceEntry = {
        id,
        name: file.name,
        type: file.type || "application/octet-stream",
        size: file.size,
        content: null,
        extractedText: "",
        isProcessing: true,
        processError: null,
        addedAt: new Date().toISOString(),
      };
      setSources((prev) => [...prev, sourceEntry]);

      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = async () => {
          const base64 = reader.result.split(",")[1];
          patchSource(id, { content: base64 });

          const isPdf =
            file.type === "application/pdf" ||
            file.name.toLowerCase().endsWith(".pdf");

          const isDocx =
            file.type.includes("officedocument.wordprocessing") ||
            file.name.toLowerCase().endsWith(".docx") ||
            file.name.toLowerCase().endsWith(".doc");

          const isTxt =
            file.type === "text/plain" ||
            file.name.toLowerCase().endsWith(".txt") ||
            file.name.toLowerCase().endsWith(".md");

          try {
            let text = "";
            if (isPdf) {
              text = await extractPdfText(base64, file.name, file.type);
            } else if (isDocx) {
              try {
                const mammoth = await import("mammoth");
                const arrayBuffer = (() => {
                  const binaryString = atob(base64);
                  const bytes = new Uint8Array(binaryString.length);
                  for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                  }
                  return bytes.buffer;
                })();
                const result = await mammoth.extractRawText({ arrayBuffer });
                text = result.value || "";
              } catch {
                text = "";
              }
            } else if (isTxt) {
              try {
                text = atob(base64);
              } catch {
                text = base64;
              }
            }

            if (!text || text.trim().length < 10) {
              patchSource(id, {
                isProcessing: false,
                processError: "Could not extract readable text from this file.",
                extractedText: "",
              });
            } else {
              patchSource(id, {
                isProcessing: false,
                extractedText: text,
                processError: null,
              });
            }
            resolve({ id, name: file.name });
          } catch (e) {
            patchSource(id, {
              isProcessing: false,
              processError: e.message || "Processing failed.",
            });
            resolve(null);
          }
        };
        reader.onerror = () => {
          patchSource(id, {
            isProcessing: false,
            processError: "Failed to read file.",
          });
          resolve(null);
        };
        reader.readAsDataURL(file);
      });
    },
    [patchSource]
  );

  const addTextSource = useCallback((text, name = "Pasted Text") => {
    const id = generateId();
    setSources((prev) => [
      ...prev,
      {
        id,
        name,
        type: "text/plain",
        size: text.length,
        content: null,
        extractedText: text,
        isProcessing: false,
        processError: null,
        addedAt: new Date().toISOString(),
      },
    ]);
    return id;
  }, []);

  const removeSource = useCallback((id) => {
    setSources((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const getSourcesContext = useCallback(
    (maxCharsPerSource = 12000) => {
      const ready = sources.filter(
        (s) => !s.isProcessing && s.extractedText && s.extractedText.trim()
      );
      if (ready.length === 0) return "";

      let ctx =
        "=== NOTEBOOK SOURCES (use these as your primary knowledge base) ===\n\n";
      ready.forEach((s, i) => {
        const text = s.extractedText.slice(0, maxCharsPerSource);
        ctx += `[Source ${i + 1}: ${s.name}]\n${text}\n\n`;
      });
      ctx += "=== END SOURCES ===\n";
      return ctx;
    },
    [sources]
  );

  return (
    <SourcesContext.Provider
      value={{
        sources,
        addFileSource,
        addTextSource,
        removeSource,
        getSourcesContext,
        hasSourcesLoaded: sources.length > 0,
      }}
    >
      {children}
    </SourcesContext.Provider>
  );
};
